import { getAuthorizationHeader } from './auth'
import type { Lead, MonthlyLeadStat } from '../types/lead'

const API_URL = 'https://back-end-artcustom.onrender.com/api/leads'

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isNaN(n) ? fallback : n
  }
  return fallback
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value : value != null ? String(value) : ''
}

function toIsoDate(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
  }
  return new Date().toISOString()
}

/** Normaliza item da API (PascalCase ou camelCase) para o modelo da tabela. */
export function mapLeadFromApi(raw: unknown): Lead | null {
  if (!raw || typeof raw !== 'object') return null

  const r = raw as Record<string, unknown>
  const id = r.Id ?? r.id
  if (id === undefined || id === null) return null

  return {
    Id: toNumber(id),
    CreatedAt: toIsoDate(r.CreatedAt ?? r.createdAt),
    Nome: toString(r.Nome ?? r.nome),
    Email: toString(r.Email ?? r.email),
    Telefone: toString(r.Telefone ?? r.telefone ?? r.numero),
    QuantidadeObjetos: toNumber(r.QuantidadeObjetos ?? r.quantidadeObjetos ?? r.quantidade),
  }
}

export function mapLeadsFromApi(data: unknown): Lead[] {
  const list = Array.isArray(data)
    ? data
    : data && typeof data === 'object'
      ? ((data as Record<string, unknown>).data ??
        (data as Record<string, unknown>).Data ??
        (data as Record<string, unknown>).leads ??
        (data as Record<string, unknown>).Leads ??
        [])
      : []

  if (!Array.isArray(list)) return []

  return list.map(mapLeadFromApi).filter((lead): lead is Lead => lead !== null)
}

export type CreateLeadPayload = {
  Nome: string
  Email: string
  Telefone: string
  QuantidadeObjetos: number
}

export async function createLead(payload: CreateLeadPayload): Promise<void> {
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Erro ao salvar solicitação'))
  }
}

async function parseApiError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as Record<string, unknown>
    const message = body.message ?? body.Message
    if (typeof message === 'string' && message.trim()) return message
  } catch {
    /* ignore */
  }
  return `${fallback} (${res.status})`
}

export async function fetchLeads(): Promise<Lead[]> {
  const authorization = getAuthorizationHeader()
  if (!authorization) {
    throw new Error('Sessão expirada. Faça login novamente.')
  }

  const res = await fetch(API_URL, {
    method: 'GET',
    headers: {
      Authorization: authorization,
    },
  })

  const body = await res.json().catch(() => null)

  if (!res.ok) {
    throw new Error(await parseApiError(res, 'Erro ao carregar leads'))
  }

  if (body && typeof body === 'object') {
    const envelope = body as Record<string, unknown>
    if (envelope.success === false) {
      const message = envelope.message ?? envelope.Message
      throw new Error(
        typeof message === 'string' ? message : 'Erro ao carregar leads',
      )
    }
  }

  return mapLeadsFromApi(body)
}

const MONTH_LABELS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const

export function getMonthlyLeadStats(leads: Lead[]): MonthlyLeadStat[] {
  const byMonth = new Map<string, MonthlyLeadStat>()

  for (const lead of leads) {
    const date = new Date(lead.CreatedAt)
    if (Number.isNaN(date.getTime())) continue

    const year = date.getFullYear()
    const month = date.getMonth()
    const key = `${year}-${String(month + 1).padStart(2, '0')}`
    const label = `${MONTH_LABELS[month]}/${year}`

    const current = byMonth.get(key) ?? { key, label, count: 0, quantidade: 0 }
    current.count += 1
    current.quantidade += lead.QuantidadeObjetos
    byMonth.set(key, current)
  }

  return [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key))
}

export function formatLeadDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
